import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Info, Star, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { countryFlag, countryName, typeLabel } from "@/lib/constants";
import ContentRow from "@/components/ContentRow";

export default function Home() {
  const [data, setData] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    api.get("/content/featured").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data?.hero?.length) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % data.hero.length), 7000);
    return () => clearInterval(t);
  }, [data]);

  if (!data) {
    return <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#E50914] border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  const hero = data.hero?.[heroIdx] || data.trending?.[0];

  return (
    <div className="pb-10">
      {hero && (
        <section className="relative h-[80vh] min-h-[520px] w-full" data-testid="hero-section">
          <img src={hero.thumbnail} alt={hero.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08070B] via-[#08070B]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08070B] via-transparent to-transparent" />
          <div className="relative h-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col justify-end pb-16 sm:pb-24">
            <div className="max-w-2xl animate-fade-up">
              <div className="flex items-center gap-2 mb-4">
                <span className="badge-glass !bg-[#E50914]/20 !border-[#E50914]/40 text-white">
                  <TrendingUp className="w-3 h-3" /> Featured
                </span>
                <span className="badge-glass">{countryFlag(hero.country)} {countryName(hero.country)}</span>
                <span className="badge-glass">{hero.language}</span>
              </div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mb-4">
                {hero.title}
              </h1>
              <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-6 line-clamp-3">{hero.description}</p>
              <div className="flex items-center gap-3 mb-6 text-sm text-white/60">
                <span className="text-[#D4AF37] font-semibold uppercase tracking-wider text-xs">{typeLabel(hero.content_type)}</span>
                {hero.rating > 0 && <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" /> {hero.rating}</span>}
                <span>{hero.age_rating}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link to={`/watch/${hero.id}`} data-testid="hero-play-btn"
                  className="flex items-center gap-2 px-7 py-3 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-colors">
                  <Play className="w-5 h-5 fill-black" /> Play Now
                </Link>
                <Link to={`/movie/${hero.id}`} data-testid="hero-info-btn"
                  className="flex items-center gap-2 px-7 py-3 rounded-full bg-white/10 border border-white/20 backdrop-blur-md font-semibold hover:bg-white/20 transition-colors">
                  <Info className="w-5 h-5" /> More Info
                </Link>
              </div>
            </div>
            {data.hero?.length > 1 && (
              <div className="flex gap-2 mt-8">
                {data.hero.map((_, i) => (
                  <button key={i} onClick={() => setHeroIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === heroIdx ? "w-8 bg-[#E50914]" : "w-4 bg-white/30"}`} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <div className="relative -mt-8 z-10 space-y-2">
        <ContentRow title="Trending Now" subtitle="What Africa is watching this week" items={data.trending} />
        <ContentRow title="Fresh Releases" subtitle="Newly added to the catalog" items={data.recent} />
        <ContentRow title="Free to Watch" subtitle="No subscription needed" items={data.free} />
      </div>
    </div>
  );
}
